import React from "react";
import { Link } from "react-router-dom";
import { 
  Linkedin, 
  Instagram, 
  Mail, 
  Phone,
  MapPin,
  BookOpen,
  Shield
} from 'lucide-react';

const Footer = () => {
  return (
    <section className="py-10 text-white sm:pt-16 lg:pt-24 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 border-t border-gray-800/50">
      <div className="px-4 mx-auto sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
          {/* Brand & Description */}
          <div className="lg:col-span-1 flex flex-col items-start">
            <img className="w-auto h-10 mb-4 drop-shadow-lg" src={`${process.env.PUBLIC_URL}/logo.png`} alt="ReferMe Logo" />

            <div className="flex items-center gap-2 mb-6">
              <span className="text-xs text-gray-400">Powered by</span>
              <a
                href="https://www.pixen.live/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-800/40 border border-gray-700/50 hover:bg-gray-800/60 hover:border-indigo-500/30 transition-all duration-300"
              >
                <img
                  className="w-auto h-4"
                  src={`${process.env.PUBLIC_URL}/pixen-logo.png`}
                  alt="Pixen Logo"
                />
              </a>
            </div> 
            
            <p className="text-sm leading-relaxed text-gray-300 mb-6">
              Your trusted companion for engineering exam prep. Access curated notes, solved PYQs, and ace your semesters with confidence.
            </p>
            
            <div className="flex items-center gap-3">
              <a
                href="https://x.com/Pixen288100?t=6aVNo6M9VWxhxXShcMj3Kg&s=09"
                target="_blank"
                rel="noopener noreferrer"
                title="X (Twitter)"
                className="flex items-center justify-center text-white transition-all duration-300 rounded-lg w-9 h-9 bg-gray-800/40 border border-gray-700/50 hover:bg-indigo-500/20 hover:border-indigo-500/30 hover:shadow-lg hover:shadow-indigo-500/10"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/crafting-with-pixen/"
                target="_blank"
                rel="noopener noreferrer"
                title="LinkedIn"
                className="flex items-center justify-center text-white transition-all duration-300 rounded-lg w-9 h-9 bg-gray-800/40 border border-gray-700/50 hover:bg-indigo-500/20 hover:border-indigo-500/30 hover:shadow-lg hover:shadow-indigo-500/10"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/craftingwithpixen?igsh=a2oxZXcxMW9udzY1"
                target="_blank"
                rel="noopener noreferrer"
                title="Instagram"
                className="flex items-center justify-center text-white transition-all duration-300 rounded-lg w-9 h-9 bg-gray-800/40 border border-gray-700/50 hover:bg-indigo-500/20 hover:border-indigo-500/30 hover:shadow-lg hover:shadow-indigo-500/10"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4 text-indigo-400" />
              <h3 className="text-sm font-semibold tracking-wider text-gray-200 uppercase">
                Quick Links
              </h3>
            </div>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-sm text-gray-300 hover:text-indigo-400 transition-colors duration-200"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/Search"
                  className="text-sm text-gray-300 hover:text-indigo-400 transition-colors duration-200"
                >
                  Browse Notes
                </Link>
              </li>
              <li>
                <Link
                  to="/SignUp"
                  className="text-sm text-gray-300 hover:text-indigo-400 transition-colors duration-200"
                >
                  Sign Up
                </Link>
              </li>
              <li>
                <Link
                  to="/login"
                  className="text-sm text-gray-300 hover:text-indigo-400 transition-colors duration-200"
                >
                  Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-indigo-400" />
              <h3 className="text-sm font-semibold tracking-wider text-gray-200 uppercase">
                Legal
              </h3>
            </div>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/terms"
                  className="text-sm text-gray-300 hover:text-indigo-400 transition-colors duration-200"
                >
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-sm text-gray-300 hover:text-indigo-400 transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-4 h-4 text-indigo-400" />
              <h3 className="text-sm font-semibold tracking-wider text-gray-200 uppercase">
                Contact Us
              </h3>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-1">Email</p>
                  <a 
                    href="mailto:support@referme.com" 
                    className="text-sm text-gray-300 hover:text-indigo-400 transition-colors duration-200"
                  >
                    craftingwithpixen@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-1">Phone</p>
                  <a 
                    href="tel:+919876543210" 
                    className="text-sm text-gray-300 hover:text-indigo-400 transition-colors duration-200"
                  >
                    +91 93094 64462
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-1">Location</p>
                  <p className="text-sm text-gray-300">
                    Pune, Maharashtra, India
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-400 text-center sm:text-left">
            © 2025 ReferMe. All Rights Reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Made with ❤️ for engineering students</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Footer;
